import {
  eventToDomainTopic,
  type DomainTopic,
  type EventName,
  type TriggerableEvent,
} from "@nexus/contracts";

export interface EventTopicGroup {
  topic: DomainTopic;
  eventNames: EventName[];
}

/**
 * A topic is never chosen — `eventToDomainTopic` derives it from the event
 * name's prefix, since routing is one topic per domain plus an `eventType`
 * attribute. Grouping by the derived topic is therefore the honest way to let
 * an operator narrow by destination without implying the two are independent.
 */
export function groupEventsByTopic(names: readonly EventName[]): EventTopicGroup[] {
  const byTopic = new Map<DomainTopic, EventName[]>();

  for (const name of names) {
    const topic = eventToDomainTopic(name);
    const group = byTopic.get(topic);
    if (group) group.push(name);
    else byTopic.set(topic, [name]);
  }

  return [...byTopic.entries()]
    .map(([topic, eventNames]) => ({ topic, eventNames: [...eventNames].sort() }))
    .sort((a, b) => a.topic.localeCompare(b.topic));
}

/**
 * Why a payload was refused. The reason is returned rather than a sentence so
 * the check stays translatable and testable — the sheet turns these into copy.
 */
export interface JsonErrorLocation {
  line: number;
  column: number;
}

export type PayloadIssue =
  /** `at` is absent when the engine's parse error carried no position. */
  | { kind: "invalid-json"; at?: JsonErrorLocation }
  | { kind: "not-an-object" }
  | { kind: "schema"; path: string; message: string };

export type PayloadCheck =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; issue: PayloadIssue };

/**
 * The sample doubles as the field list: an event whose sample is `{}` takes no
 * payload, so the sheet can skip the editor entirely and offer one click.
 */
export function hasPayloadFields(entry: TriggerableEvent): boolean {
  return Object.keys(entry.sample).length > 0;
}

export function formatSamplePayload(entry: TriggerableEvent): string {
  return JSON.stringify(entry.sample, null, 2);
}

/**
 * Runs the same schema the API will run, so the sheet can refuse a bad payload
 * before it costs a round trip. The API re-checks regardless — this is a
 * courtesy, not the boundary.
 */
export function checkPayload(entry: TriggerableEvent, text: string): PayloadCheck {
  const parsed = checkCustomPayload(text);
  if (!parsed.ok) return parsed;

  const result = entry.schema.safeParse(parsed.value);
  if (!result.success) {
    const issue = result.error.issues[0];
    return {
      ok: false,
      issue: {
        kind: "schema",
        path: issue?.path.join(".") ?? "",
        message: issue?.message ?? "",
      },
    };
  }

  return { ok: true, value: result.data };
}

/**
 * The from-scratch mode has no template to validate against, so the only
 * check the sheet can make is "this is a JSON object". The consumer of the
 * event owns everything beyond that.
 */
export function checkCustomPayload(text: string): PayloadCheck {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text.trim() === "" ? "{}" : text);
  } catch (error) {
    const at = locateJsonError(text, error);
    return { ok: false, issue: at ? { kind: "invalid-json", at } : { kind: "invalid-json" } };
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, issue: { kind: "not-an-object" } };
  }

  return { ok: true, value: parsed as Record<string, unknown> };
}

/**
 * Reads where parsing broke out of the engine's message, since neither the
 * offset nor the line is exposed on the error object. V8 and Firefox both name
 * a line and column for structural errors; the "unexpected token" family
 * carries no location at all, which is why the caller treats it as optional.
 */
function locateJsonError(text: string, error: unknown): JsonErrorLocation | undefined {
  const message = error instanceof Error ? error.message : "";

  const lineColumn = /line (\d+) column (\d+)/.exec(message);
  if (lineColumn) return { line: Number(lineColumn[1]), column: Number(lineColumn[2]) };

  const position = /position (\d+)/.exec(message);
  if (!position) return undefined;

  const offset = Math.min(Number(position[1]), text.length);
  const consumed = text.slice(0, offset).split("\n");
  return { line: consumed.length, column: (consumed[consumed.length - 1]?.length ?? 0) + 1 };
}

/**
 * Re-indents what the operator typed. Returns null rather than throwing so the
 * editor can simply leave unparseable text alone and let the error line speak.
 */
export function formatPayloadText(text: string): string | null {
  try {
    return JSON.stringify(JSON.parse(text.trim() === "" ? "{}" : text), null, 2);
  } catch {
    return null;
  }
}

/**
 * A scratch-mode event has no schema to describe it, so the closest thing to a
 * field list is what was published last. The values are blanked because the
 * point is the shape — carrying a previous run's tenant id over is how someone
 * re-syncs the wrong tenant by accident.
 */
export function blankPayloadShape(payload: Record<string, unknown>): Record<string, unknown> {
  const shape: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    shape[key] = blankValue(value);
  }
  return shape;
}

function blankValue(value: unknown): unknown {
  if (Array.isArray(value)) return [];
  if (typeof value === "object" && value !== null) {
    return blankPayloadShape(value as Record<string, unknown>);
  }
  if (typeof value === "number") return 0;
  if (typeof value === "boolean") return false;
  return "";
}
